/**
 * 데일리 브리핑 API — 환율/자산요약/당일손익/뉴스
 */
import { apiClient } from "./client";

export interface ExchangeRateVO {
  current_rate: number;
  prev_rate: number;
  change: number;
  change_percent: number;
}

/** 환율 히스토리 행 (GET /v1/portfolio/exchange-rate/history) */
export interface ExchangeRateHistoryVO {
  date: string;
  rate: number;
}

export interface DailyAssetStatusVO {
  period: { start_date: string; end_date: string };
  summary: {
    stock: { total_investment: number; total_current_value: number };
    fund: { total_investment: number; total_current_value: number };
    fixed_income: { total_investment: number; total_current_value: number };
    total: { total_investment: number; total_current_value: number };
  };
  daily: {
    date: string;
    stock: { net_flow: number; current_value: number };
    fund: { net_flow: number; current_value: number };
    fixed_income: { net_flow: number; current_value: number };
  }[];
}

export interface TodayPriceVO {
  stock_id: string;
  trading_date: string;
  open_price: number;
  close_price: number;
  adj_close_price: number;
  high_price: number;
  low_price: number;
  volume: number;
}

/** AI 데일리 브리핑 (GET /v1/insights/daily-briefing) */
export interface DailyBriefingVO {
  briefing: string;
  generated_at: string;
  from_cache: boolean;
  summary: {
    date: string;
    fx: { rate: number; change: number; change_percent: number };
    total: { value: number; invest: number; day_change: number; day_change_percent: number };
    stock: {
      kr_prev: number;
      kr_change: number;
      us_prev: number;
      us_change: number;
      prev: number;
      change: number;
      change_percent: number;
    };
    top_gainers: { stock_id: string; stock_name: string; change_percent_krw: number; profit_krw: number }[];
    top_losers: { stock_id: string; stock_name: string; change_percent_krw: number; profit_krw: number }[];
    dividend?: {
      month: number;
      month_total_krw: number;
      month_actual_krw: number;
      annual_total_krw: number;
      coverage_ratio: number;
    } | null;
    target_return?: {
      target_rate: number;
      adjusted_return: number;
      gap_rate: number;
      current_rate: number;
    } | null;
  };
}

export const briefApi = {
  /** 상세 환율 */
  getExchangeRate: () => apiClient.get<ExchangeRateVO>("/v1/portfolio/exchange-rate/detailed"),

  /** 환율 히스토리 (일별 시계열) */
  getExchangeRateHistory: (days = 90) =>
    apiClient.get<ExchangeRateHistoryVO[]>("/v1/portfolio/exchange-rate/history", { days }),

  /** 일별 자산 상태 (요약 + 흐름) */
  getDailyAssetStatus: (userIds: string[], days = 7) => {
    const end = new Date();
    const start = new Date(Date.now() - days * 86400_000);
    return apiClient.get<DailyAssetStatusVO>("/v1/portfolio/daily-asset-status", {
      user_ids: userIds.join(","),
      start_date: start.toISOString().slice(0, 10),
      end_date: end.toISOString().slice(0, 10),
    });
  },

  /** 오늘 주가 (stock_ids 필요) */
  getTodayPrices: (stockIds: string[]) =>
    apiClient.get<TodayPriceVO[]>("/v1/stocks/today-prices", {
      stock_ids: stockIds.join(","),
    }),

  /** AI 데일리 브리핑 (캐시 우선) */
  getDailyBriefing: (userIds: string[], summaryOnly = false) =>
    apiClient.get<DailyBriefingVO>("/v1/insights/daily-briefing", {
      user_ids: userIds.join(","),
      ...(summaryOnly ? { summary_only: true } : {}),
    }, { timeout: summaryOnly ? undefined : 180_000 }), // LLM 첫 생성 60~90초 — 기본 30초 타임아웃이 대시보드 실패 원인 (2026-08-19). summary_only는 즉시 응답이라 기본값

  /** AI 데일리 브리핑 강제 재생성 */
  refreshDailyBriefing: (userIds: string[]) =>
    apiClient.get<DailyBriefingVO>("/v1/insights/daily-briefing", {
      user_ids: userIds.join(","),
      force_refresh: true,
    }, { timeout: 180_000 }),
};
